import { useState } from 'react';

import { useStore } from '@nanostores/react';

import { usePatientPrescriptionsQuery } from '@/entities/rx';
import { useUserInfoQuery } from '@/entities/user-info';
import { PhrTableTab } from '@/features/phr-table';
import { RxPrescriptionsTable } from '@/features/rx-prescriptions-table';
import {
  pullMedHistory,
  type PullMedHistoryResponseT,
  type SureScriptsMedHistoryItemT,
} from '@/shared/api/lotus';
import { SANDBOX_SIMPLIFIED_WORKSPACE_ENABLED } from '@/shared/config/flags';
import {
  $patientWorkspaceSearchTarget,
  clearPatientWorkspaceSearchTarget,
} from '@/shared/stores/ui';
import { WorkspaceSection } from '@/shared/ui/primitives';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/shadcn/dialog';
import { Separator } from '@/shared/ui/shadcn/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

interface PatientMedicationsTabProps {
  userId: string;
}

export function PatientMedicationsTab({ userId }: PatientMedicationsTabProps) {
  const searchTarget = useStore($patientWorkspaceSearchTarget);
  const rxQuery = usePatientPrescriptionsQuery(userId);
  const hasPrescriptions = (rxQuery.data?.prescriptions?.length ?? 0) > 0;
  const openPrescriptionId =
    searchTarget?.resource === 'rx:prescriptions' ? searchTarget.resultId : undefined;

  return (
    <div className="space-y-6" aria-label="Medications">
      {hasPrescriptions && (
        <>
          <WorkspaceSection title="Prescribed Through Lotus Health">
            <RxPrescriptionsTable
              userId={userId}
              openPrescriptionId={openPrescriptionId}
              onConsumeOpenPrescription={clearPatientWorkspaceSearchTarget}
            />
          </WorkspaceSection>

          <Separator />
        </>
      )}

      {!SANDBOX_SIMPLIFIED_WORKSPACE_ENABLED && (
        <>
          <SureScriptsMedHistorySection userId={userId} />
          <Separator />
        </>
      )}

      <WorkspaceSection title="EHR Medications">
        <PhrTableTab userId={userId} tableKey="medications" />
      </WorkspaceSection>
    </div>
  );
}

function SureScriptsMedHistorySection({ userId }: { userId: string }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PullMedHistoryResponseT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const userInfoQuery = useUserInfoQuery(userId);

  const medHistoryConsentAt: string | null =
    ((userInfoQuery.data?.patientInfo as Record<string, unknown> | undefined)
      ?.med_history_consent_at as string | null) ?? null;

  const handlePull = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await pullMedHistory(userId);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to pull medication history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceSection
      title="Surescripts Medication History"
      description="PBM insurance claims from the Surescripts network"
      action={
        <Button variant="outline" size="sm" onClick={handlePull} disabled={loading}>
          {loading ? 'Pulling…' : data ? 'Refresh' : 'Pull History'}
        </Button>
      }
    >
      <MedHistoryConsentBanner consentAt={medHistoryConsentAt} />

      {error && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="mt-3 space-y-2">
          <div className="rounded border border-amber-200/60 bg-amber-50/40 px-3 py-2 text-xs text-amber-800 dark:border-amber-700/30 dark:bg-amber-950/20 dark:text-amber-300">
            {data.disclaimer}
          </div>

          {data.resultDescription && (
            <Alert>
              <AlertDescription>{data.resultDescription}</AlertDescription>
            </Alert>
          )}

          {data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No medication history records returned.</p>
          ) : (
            <MedHistoryTable items={data.items} />
          )}
        </div>
      )}
    </WorkspaceSection>
  );
}

function MedHistoryConsentBanner({ consentAt }: { consentAt: string | null }) {
  if (!consentAt) {
    return (
      <div className="mt-2 rounded border border-yellow-300/40 bg-yellow-50/50 px-2.5 py-1.5 text-xs text-yellow-800 dark:border-yellow-600/30 dark:bg-yellow-950/20 dark:text-yellow-300">
        Med history consent not recorded
      </div>
    );
  }

  const formatted = new Date(consentAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div className="mt-2 rounded border border-green-300/40 bg-green-50/50 px-2.5 py-1.5 text-xs text-green-800 dark:border-green-600/30 dark:bg-green-950/20 dark:text-green-300">
      Med history consent granted {formatted}
    </div>
  );
}

function formatDxCode(code: string | null): string {
  if (!code) {
    return '—';
  }
  const trimmed = code.trim();
  if (trimmed.includes('.') || trimmed.length <= 3) {
    return trimmed;
  }
  return `${trimmed.slice(0, 3)}.${trimmed.slice(3)}`;
}

const DEA_SCHEDULE_LABELS: Record<number, string> = {
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
};

function MedHistoryTable({ items }: { items: SureScriptsMedHistoryItemT[] }) {
  const [detailItem, setDetailItem] = useState<SureScriptsMedHistoryItemT | null>(null);

  const sorted = [...items].sort((a, b) => {
    const dateA = a.lastFillDate ?? a.effectiveDate ?? '';
    const dateB = b.lastFillDate ?? b.effectiveDate ?? '';
    return dateB.localeCompare(dateA);
  });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">#</TableHead>
            <TableHead>Display Name</TableHead>
            <TableHead>Generic Name</TableHead>
            <TableHead>NDC</TableHead>
            <TableHead>RxNorm</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Qty UoM</TableHead>
            <TableHead>Subs</TableHead>
            <TableHead>Days Supply</TableHead>
            <TableHead>Refills</TableHead>
            <TableHead>Strength</TableHead>
            <TableHead>Form</TableHead>
            <TableHead>Route</TableHead>
            <TableHead>Dx Code</TableHead>
            <TableHead className="whitespace-nowrap">DEA Sch.</TableHead>
            <TableHead>Classification</TableHead>
            <TableHead className="min-w-[180px]">Directions</TableHead>
            <TableHead>Last Fill</TableHead>
            <TableHead>Effective</TableHead>
            <TableHead>Written</TableHead>
            <TableHead>Expiration</TableHead>
            <TableHead>Payer</TableHead>
            <TableHead className="min-w-[120px]">Notes</TableHead>
            <TableHead>Rx#</TableHead>
            <TableHead>Compound Seq.</TableHead>
            <TableHead className="w-[60px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item, idx) => (
            <TableRow key={`${item.ndc ?? item.displayName ?? ''}-${idx}`}>
              <TableCell className="align-top text-xs text-muted-foreground">{idx + 1}</TableCell>
              <TableCell className="align-top text-sm font-medium">
                {item.displayName ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs">{item.genericDrugName ?? '—'}</TableCell>
              <TableCell className="align-top font-mono text-xs">{item.ndc ?? '—'}</TableCell>
              <TableCell className="align-top font-mono text-xs">{item.rxcui ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.quantity ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.dispenseUnit ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">
                {item.noSubstitutions != null ? (item.noSubstitutions ? 'Yes' : 'No') : '—'}
              </TableCell>
              <TableCell className="align-top text-xs">{item.daysSupply ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.refills ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.strength ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.doseForm ?? '—'}</TableCell>
              <TableCell className="align-top text-xs">{item.route ?? '—'}</TableCell>
              <TableCell className="align-top font-mono text-xs">
                {formatDxCode(item.diagnosisCode)}
              </TableCell>
              <TableCell className="align-top text-xs whitespace-nowrap">
                {item.schedule ? (DEA_SCHEDULE_LABELS[item.schedule] ?? `${item.schedule}`) : '—'}
              </TableCell>
              <TableCell className="align-top text-xs">{item.drugClassification ?? '—'}</TableCell>
              <TableCell className="align-top text-xs min-w-[180px]">
                {item.directions ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs text-muted-foreground whitespace-nowrap">
                {item.lastFillDate ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs text-muted-foreground whitespace-nowrap">
                {item.effectiveDate ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs text-muted-foreground whitespace-nowrap">
                {item.writtenDate ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs text-muted-foreground whitespace-nowrap">
                {item.expirationDate ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs text-muted-foreground">
                {item.payer ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs">{item.pharmacyNotes ?? '—'}</TableCell>
              <TableCell className="align-top font-mono text-xs">
                {item.pharmacyRxNumber ?? '—'}
              </TableCell>
              <TableCell className="align-top text-xs">
                {item.compoundIngredientSequence ?? '—'}
              </TableCell>
              <TableCell className="align-top">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setDetailItem(item)}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <MedHistoryDetailDialog item={detailItem} onClose={() => setDetailItem(null)} />
    </>
  );
}

const DETAIL_FIELDS: { key: keyof SureScriptsMedHistoryItemT; label: string }[] = [
  { key: 'displayName', label: 'Display Name' },
  { key: 'genericDrugName', label: 'Generic Drug Name' },
  { key: 'ndc', label: 'NDC' },
  { key: 'rxcui', label: 'RxCUI' },
  { key: 'strength', label: 'Strength' },
  { key: 'doseForm', label: 'Dose Form' },
  { key: 'route', label: 'Route' },
  { key: 'directions', label: 'Directions' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'dispenseUnit', label: 'Dispense Unit' },
  { key: 'daysSupply', label: 'Days Supply' },
  { key: 'refills', label: 'Refills' },
  { key: 'noSubstitutions', label: 'No Substitutions' },
  { key: 'lastFillDate', label: 'Last Fill Date' },
  { key: 'effectiveDate', label: 'Effective Date' },
  { key: 'writtenDate', label: 'Written Date' },
  { key: 'expirationDate', label: 'Expiration Date' },
  { key: 'payer', label: 'Payer' },
  { key: 'drugClassification', label: 'Drug Classification' },
  { key: 'diagnosisCode', label: 'Diagnosis Code' },
  { key: 'schedule', label: 'DEA Schedule' },
  { key: 'pharmacyNotes', label: 'Pharmacy Notes' },
  { key: 'pharmacyRxNumber', label: 'Pharmacy Rx Number' },
  { key: 'compoundIngredientSequence', label: 'Compound Ingredient Sequence' },
  { key: 'patientMedicationHistoryId', label: 'Medication History ID' },
  { key: 'diagnosisQualifier', label: 'Diagnosis Qualifier' },
  { key: 'dispensableDrugId', label: 'Dispensable Drug ID' },
  { key: 'brand', label: 'Brand' },
  { key: 'otc', label: 'OTC' },
];

function MedHistoryDetailDialog({
  item,
  onClose,
}: {
  item: SureScriptsMedHistoryItemT | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={item != null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium">
            {item?.displayName ?? item?.genericDrugName ?? 'Medication Details'}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
            {DETAIL_FIELDS.map(({ key, label }) => {
              const value = item[key];
              if (value == null) {
                return null;
              }
              return (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground whitespace-nowrap py-0.5">{label}</dt>
                  <dd className="font-mono py-0.5 break-all">
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}
